import { useState, useEffect, useCallback, useMemo } from 'react'
import { LS_COUNTRIES_KEY, LS_COUNTRIES_KEY_BASE } from 'constants/global'
import { clearMatchingLocalStorageEntries, convertResidenceAlpha3ToLongForm, getUnknownDisplayText } from 'utils'
import { Option } from 'components/generic/SearchFieldWTN'

export interface Country {
  ['name']: string
  ['alpha-2']: string
  ['alpha-3']: string
  ['ioc-code']: string | null
  ['country-code']: string | null
  ['nationality']: string
  ['isActive']: boolean
}

type CountryDict = { [code: string]: Country }

export const useCountries = () => {
  const [countriesDict, setCountriesDict] = useState<CountryDict>({})
  const [activeCountriesList, setActiveCountriesList] = useState<Country[]>([])

  useEffect(() => {
    ;(async () => {
      try {
        let countries: CountryDict
        const saved = localStorage.getItem(LS_COUNTRIES_KEY)

        if (saved) {
          countries = JSON.parse(saved)
        } else {
          /* Remove outdated countries lists */
          clearMatchingLocalStorageEntries(LS_COUNTRIES_KEY_BASE)
          countries = await require('../api/all-countries.json')
          localStorage.setItem(LS_COUNTRIES_KEY, JSON.stringify(countries))
        }

        setCountriesDict(countries)
      } catch (error) {
        console.warn(error)
      }
    })()
  }, [setCountriesDict])

  useEffect(() => {
    const getActiveCountries = () => {
      const countryArr = Object.values(countriesDict)
      return countryArr.filter(country => country.isActive)
    }

    setActiveCountriesList(getActiveCountries())
  }, [countriesDict])

  const getCountryName = useCallback(
    (alpha3: string | undefined): string => {
      const country = alpha3 && countriesDict[alpha3.toUpperCase()]
      return (country && country.name) || getUnknownDisplayText()
    },
    [countriesDict]
  )

  const getNationality = useCallback(
    (alpha3: string | undefined): string => {
      const country = alpha3 && countriesDict[alpha3.toUpperCase()]
      return (country && country.nationality) || getUnknownDisplayText()
    },
    [countriesDict]
  )

  const isValidCountryCode = useCallback(
    (alpha3: string | undefined): boolean => {
      if (!alpha3 || typeof alpha3 !== 'string') return false
      const country = countriesDict[alpha3.toUpperCase()]
      return country?.isActive || false
    },
    [countriesDict]
  )

  const getAlpha2 = useCallback(
    (alpha3: string | undefined): string => {
      const country = alpha3 && countriesDict[alpha3.toUpperCase()]
      return (country && country['alpha-2']) || getUnknownDisplayText()
    },
    [countriesDict]
  )

  const residenceOptions = useMemo(() => {
    return activeCountriesList
      .map(country => ({
        value: convertResidenceAlpha3ToLongForm(country['alpha-3']),
        label: country.name || '',
      }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label))
  }, [activeCountriesList])

  const nationalityOptions = useMemo(() => {
    return activeCountriesList
      .map(country => ({
        value: country['alpha-3'],
        label: country.nationality || '',
      }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label))
  }, [activeCountriesList])

  return {
    isValidCountryCode,
    getCountryName,
    getNationality,
    getAlpha2,
    activeCountriesList,
    countriesDict,
    residenceOptions,
    nationalityOptions,
  }
}
